import { type FontSpec } from "chart.js";
import {
  type Scriptable,
  type ScaleValue,
  type PartialEventContext,
  type AnnotationOptions,
} from "chartjs-plugin-annotation";
import moment from "moment";

import {
  type ChartDataManagerPluginOptions,
  getChartData,
} from "../plugins/chart-data-manager-plugin";

type SampleDataField = "inSampleData" | "outOfSampleData";

export const makeFont = (font: Partial<FontSpec> = {}) => ({
  family: "Commons Pro",
  size: 13,
  weight: "bold",
  ...font,
});

export const position = {
  x: "start",
  y: "start",
} as const;

const getDateRangeForData = (
  chartData: ChartDataManagerPluginOptions,
  field: SampleDataField,
): {
  startTime: number;
  endTime: number;
} | null => {
  if (chartData?.[field]?.startDate && chartData?.[field]?.endDate) {
    return {
      startTime: chartData[field].startDate.getTime(),
      endTime: chartData[field].endDate.getTime(),
    };
  }

  return null;
};

const chartIncludesDateRange = (
  ctx: PartialEventContext,
  field: SampleDataField,
) => {
  const chartData = getChartData(ctx.chart);
  const fieldDateRange = getDateRangeForData(chartData, field);

  if (!fieldDateRange) {
    return false;
  }

  const { startTime, endTime } = fieldDateRange;
  const periodStartTime = ctx.chart.scales.x.min;
  const periodEndTime = ctx.chart.scales.x.max;

  return periodEndTime >= startTime && !(endTime <= periodStartTime);
};

export const getInSampleLabelPosition = (
  ctx: PartialEventContext,
): {
  x: "end" | "start";
  y: "start";
} => {
  if (!chartIncludesDateRange(ctx, "inSampleData")) {
    return {
      x: "end",
      y: "start",
    };
  }

  return {
    x: "start",
    y: "start",
  };
};

export const getConflictingYAdjust = (ctx: PartialEventContext) => {
  const chartData = getChartData(ctx.chart);

  if (!chartData) {
    return 0;
  }

  const inSampleRange = getDateRangeForData(chartData, "inSampleData");
  const outOfSampleRange = getDateRangeForData(chartData, "outOfSampleData");

  if (!outOfSampleRange || !inSampleRange) {
    return 0;
  }

  if (!chartIncludesDateRange(ctx, "outOfSampleData")) {
    return 0;
  }

  const totalChartWidth = ctx.chart.scales.x.max - ctx.chart.scales.x.min;
  const inOutWidth = outOfSampleRange.startTime - ctx.chart.scales.x.min;

  return inOutWidth / totalChartWidth < 0.3 ? 40 : 0;
};

export const getOutOfSampleLabelPosition = (
  ctx: PartialEventContext,
): {
  x: "start" | "end";
  y: "start";
} => {
  const chartData = getChartData(ctx.chart);

  const defaultPosition = {
    x: "start",
    y: "start",
  } as const;

  if (!chartData) {
    return defaultPosition;
  }

  const outOfSampleRange = getDateRangeForData(chartData, "outOfSampleData");

  if (!outOfSampleRange) {
    return defaultPosition;
  }

  if (!chartIncludesDateRange(ctx, "outOfSampleData")) {
    return defaultPosition;
  }

  const totalChartWidth = ctx.chart.scales.x.max - ctx.chart.scales.x.min;
  const inOutWidth = outOfSampleRange.startTime - ctx.chart.scales.x.min;

  if (inOutWidth / totalChartWidth < 0.75) {
    return defaultPosition;
  }

  return {
    x: "end",
    y: "start",
  };
};

export const getOutOfSampleXValue = (ctx: PartialEventContext) => {
  if (!chartIncludesDateRange(ctx, "outOfSampleData")) {
    const chartData = getChartData(ctx.chart);

    if (!chartData) {
      return ctx.chart.scales.x.min;
    }

    const viewableDateRange = chartData.dateRange;

    const { outOfSampleData } = chartData;
    return new Date(viewableDateRange.startDate).getTime() >=
      new Date(outOfSampleData.startDate).getTime()
      ? ctx.chart.scales.x.min
      : getOutOfXSampleStartTime(ctx);
  }

  return getOutOfXSampleStartTime(ctx);
};

export const formattedSampleDates =
  (field: SampleDataField): Scriptable<string, PartialEventContext> =>
  (ctx) => {
    const chartData = getChartData(ctx.chart);
    if (!chartData?.[field]?.startDate) {
      return "";
    }

    const dateRange = chartData[field];
    const { startDate, endDate } = dateRange;
    return `${moment(startDate).format("MMM D, YYYY")} to ${moment(
      endDate,
    ).format("MMM D, YYYY")}`;
  };

export const getOutOfXSampleStartTime = (ctx: PartialEventContext) => {
  const chartData = getChartData(ctx.chart);
  if (!chartData?.outOfSampleData?.startDate) {
    return ctx.chart.scales.x.max;
  }

  const { outOfSampleData } = chartData;
  const outOfSampleStart = new Date(outOfSampleData.startDate).getTime();
  return ctx.chart.scales.x.min > outOfSampleStart
    ? ctx.chart.scales.x.min
    : outOfSampleStart;
};

export const getOutOfYAdjustmentSampleStartTime: Scriptable<
  number,
  PartialEventContext
> = (ctx) => {
  const chartData = getChartData(ctx.chart);

  if (!chartData?.outOfSampleData?.startDate) {
    return 0;
  }

  const viewableDateRange = chartData.dateRange;

  const { outOfSampleData } = chartData;
  return new Date(viewableDateRange.startDate).getTime() >=
    new Date(outOfSampleData.startDate).getTime()
    ? 0
    : getConflictingYAdjust(ctx);
};

export const commonMapeAnnotationOptions = (
  field: SampleDataField,
  {
    xValue,
    yValue,
    yAdjust,
  }: {
    xValue: Scriptable<ScaleValue, PartialEventContext>;
    yValue: Scriptable<ScaleValue, PartialEventContext>;
    yAdjust: number;
  },
): AnnotationOptions => ({
  type: "label",
  position,
  xValue,
  yValue,
  yAdjust,
  xAdjust: 8,
  content: ({ chart }) => {
    const canvas = document.createElement("canvas");
    canvas.width = 62;
    canvas.height = 12;
    const ctx = canvas.getContext("2d");
    const chartData = getChartData(chart);
    if (!ctx || !chartData?.[field]) {
      return "";
    }

    const mape = chartData[field].mape;
    const font = makeFont({ weight: "normal", size: 10 });
    ctx.fillStyle = "#7081FF";
    ctx.font = `${font.weight} ${font.size}px ${font.family}`;
    ctx.fillText(`MAPE = ${mape}`, 0, 10);
    ctx.fillStyle = "#666";
    return canvas;
  },
  borderColor: "#E0E2E6",
  borderRadius: 4,
  borderWidth: 1,
  color: "#7081FF",
  padding: {
    top: 2,
    bottom: 2,
    left: 4,
    right: 4,
  },
  font: makeFont({
    size: 10,
    weight: "normal",
  }),
});
